import { relations } from 'drizzle-orm'
import { boolean, index, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { users } from '../schema'
import { tenants } from './tenants'
import { trainingSchedules, trainings } from './training'

// Training Enrollments
export const trainingEnrollments = pgTable(
  'training_enrollments',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    trainingId: uuid('training_id')
      .notNull()
      .references(() => trainings.id, { onDelete: 'cascade' }),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id),
    status: text('status').notNull().default('applied'),
    enrolledAt: timestamp('enrolled_at').defaultNow(),
    completedAt: timestamp('completed_at'),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
    updatedAt: timestamp('updated_at').defaultNow().notNull(),
  },
  (table) => ({
    trainingUserUnique: unique().on(table.trainingId, table.userId),
    trainingIdIdx: index('training_enrollments_training_id_idx').on(table.trainingId),
    userIdIdx: index('training_enrollments_user_id_idx').on(table.userId),
    tenantIdIdx: index('training_enrollments_tenant_id_idx').on(table.tenantId),
    tenantStatusIdx: index('training_enrollments_tenant_status_idx').on(
      table.tenantId,
      table.status,
    ),
  }),
)

// Enrollment Attendances
export const enrollmentAttendances = pgTable(
  'enrollment_attendances',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    enrollmentId: uuid('enrollment_id')
      .notNull()
      .references(() => trainingEnrollments.id, { onDelete: 'cascade' }),
    scheduleId: uuid('schedule_id')
      .notNull()
      .references(() => trainingSchedules.id, { onDelete: 'cascade' }),
    attended: boolean('attended').notNull().default(false),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    createdAt: timestamp('created_at').defaultNow().notNull(),
  },
  (table) => ({
    enrollmentScheduleUnique: unique().on(table.enrollmentId, table.scheduleId),
    enrollmentIdIdx: index('enrollment_attendances_enrollment_id_idx').on(table.enrollmentId),
    tenantIdIdx: index('enrollment_attendances_tenant_id_idx').on(table.tenantId),
  }),
)

// Relations
export const trainingEnrollmentsRelations = relations(trainingEnrollments, ({ one, many }) => ({
  training: one(trainings, {
    fields: [trainingEnrollments.trainingId],
    references: [trainings.id],
  }),
  user: one(users, {
    fields: [trainingEnrollments.userId],
    references: [users.id],
  }),
  enrollmentAttendances: many(enrollmentAttendances),
}))

export const enrollmentAttendancesRelations = relations(enrollmentAttendances, ({ one }) => ({
  enrollment: one(trainingEnrollments, {
    fields: [enrollmentAttendances.enrollmentId],
    references: [trainingEnrollments.id],
  }),
  schedule: one(trainingSchedules, {
    fields: [enrollmentAttendances.scheduleId],
    references: [trainingSchedules.id],
  }),
}))

// Type exports
export type TrainingEnrollment = typeof trainingEnrollments.$inferSelect
export type NewTrainingEnrollment = typeof trainingEnrollments.$inferInsert

export type EnrollmentAttendance = typeof enrollmentAttendances.$inferSelect
export type NewEnrollmentAttendance = typeof enrollmentAttendances.$inferInsert
